'use client'

import { buttonClassNames } from '@components/primitives/Button'
import { Icon } from '@components/primitives/Icon'
import Link from '@components/primitives/Link'
import { classMerge } from '@components/utilities/classMerge'
import { faFilterSlash } from '@fortawesome/pro-regular-svg-icons/faFilterSlash'
import Routes from '@lib/routes'
import urlWithQueryParams from '@lib/routes/helpers/urlWithQueryParams'
import { trackEvent } from '@lib/tracking'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import type { NoResultHint } from '@modules/trials/search/const'
import { NO_RESULT_HINTS } from '@modules/trials/search/const'
import omit from 'lodash/omit'
import { usePathname, useSearchParams } from 'next/navigation'

export default function NoResultsSection() {
  const searchParams = new URLSearchParams(useSearchParams() ?? {})
  const pathname = usePathname() ?? ''

  // Get visible filters based on current state
  const visibleHints = NO_RESULT_HINTS.filter((hint) =>
    hint.shouldShow({ searchParams }),
  )

  return (
    <div className='mt-4 block justify-center rounded-lg p-4 text-lg lg:mt-8'>
      <div className='mx-auto w-[343px] items-center justify-start lg:w-fit'>
        <Icon
          className='mb-4 size-6 rounded-full bg-primary100/50 p-4 text-primary600 lg:size-8 lg:p-6'
          icon={faFilterSlash}
        />
        <h5 className='mb-2 text-base font-bold antialiased'>
          No Clinical Trials Found
        </h5>
        <p className='text-sm antialiased'>
          No results match your current search. Try removing one or more filters
          below, or updating your condition to see more clinical trials.
        </p>

        {searchParams.size > 0 && (
          <HintContainer
            hints={visibleHints}
            pathname={pathname}
            searchParams={searchParams}
          />
        )}

        <div className='mt-6 flex flex-wrap gap-6'>
          <Link
            className='text-sm font-bold underline decoration-neutral300 underline-offset-2 antialiased'
            href={Routes.search}
          >
            Clear All Filters
          </Link>
          <Link
            className='text-sm font-bold underline decoration-neutral300 underline-offset-2 antialiased'
            href={pathname ?? ''}
          >
            Clear All Filters, Condition, and Location
          </Link>
        </div>
      </div>
    </div>
  )
}

function HintContainer({
  hints,
  pathname,
  searchParams,
}: {
  hints: NoResultHint[]
  pathname: string
  searchParams: URLSearchParams
}) {
  if (hints.length === 0) return null

  return (
    <div className='mt-4 flex flex-wrap gap-2'>
      {hints.map((hint, index) => (
        <HintButton
          key={index}
          label={hint.label}
          param={hint.key}
          pathname={pathname}
          searchParams={searchParams}
        />
      ))}
    </div>
  )
}

function HintButton({
  label,
  param,
  pathname,
  searchParams,
}: {
  label: string
  param: string
  pathname: string
  searchParams: URLSearchParams
}) {
  const linkWithoutFilter = urlWithQueryParams(pathname, {
    ...omit(Object.fromEntries(searchParams.entries()), param),
  })

  return (
    <Link
      className={classMerge(
        buttonClassNames.outline.white,
        'w-fit rounded-lg border-[1.5px] px-4 shadow',
      )}
      href={linkWithoutFilter}
      onClick={() => {
        trackEvent(TrackingEvents.SEARCH_LANDER_NO_RESULTS_HINT_CLICKED, {
          val: param,
        })
      }}
    >
      Remove {label}
    </Link>
  )
}
